/**
 * OfflineBanner Component
 *
 * A banner that displays when the user is offline, providing visual feedback
 * about the connection status and indicating that cached data is being displayed.
 *
 * Features:
 * - Animates in/out when connection status changes
 * - Shows a warning icon and clear messaging
 * - Non-intrusive placement at top of viewport
 * - Accessible with proper ARIA attributes
 *
 * Usage:
 * ```tsx
 * import { OfflineBanner } from "@/components/ui/offline-banner";
 * import { useOnlineStatus } from "@/hooks";
 *
 * function App() {
 *   const { isOffline } = useOnlineStatus();
 *
 *   return (
 *     <div>
 *       <OfflineBanner show={isOffline} />
 *       <MainContent />
 *     </div>
 *   );
 * }
 * ```
 */

import { useEffect, useState } from "react";
import { WifiOff, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

// ============================================================================
// Types
// ============================================================================

export interface OfflineBannerProps {
  /** Whether to show the banner */
  show: boolean;
  /** Optional className for additional styling */
  className?: string;
  /** Optional custom message to display */
  message?: string;
  /** Whether to show a retry button */
  showRetryButton?: boolean;
  /** Callback when retry button is clicked */
  onRetry?: () => void;
}

// ============================================================================
// Component
// ============================================================================

/**
 * OfflineBanner
 *
 * Displays a warning banner when the user loses their internet connection.
 * The banner slides in from the top and provides clear feedback about the
 * offline state.
 *
 * @param props - Component props
 * @returns The OfflineBanner component
 *
 * @example
 * ```tsx
 * // Basic usage
 * <OfflineBanner show={isOffline} />
 *
 * // With custom message
 * <OfflineBanner
 *   show={isOffline}
 *   message="No internet connection. Showing cached data."
 * />
 *
 * // With retry button
 * <OfflineBanner
 *   show={isOffline}
 *   showRetryButton
 *   onRetry={() => window.location.reload()}
 * />
 * ```
 */
export function OfflineBanner({
  show,
  className,
  message = "You're offline. Showing cached data.",
  showRetryButton = false,
  onRetry,
}: OfflineBannerProps) {
  // Track visibility for exit animation
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (show) {
      setShouldRender(true);
      // Small delay to ensure the element is in the DOM before animating
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      // Wait for exit animation before removing from DOM
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [show]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        // Base styles
        "fixed top-0 left-0 right-0 z-[100]",
        "flex items-center justify-center gap-2",
        "px-4 py-2.5",
        "bg-amber-500 text-amber-950",
        "shadow-md",
        // Animation
        "transition-transform duration-300 ease-out",
        isVisible ? "translate-y-0" : "-translate-y-full",
        // Safe area for notched devices
        "safe-area-inset-top",
        className
      )}
    >
      <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="text-sm font-medium">{message}</span>
      {showRetryButton && onRetry && (
        <button
          onClick={onRetry}
          className={cn(
            "ml-2 inline-flex items-center gap-1",
            "rounded-md px-2 py-1",
            "text-xs font-semibold",
            "bg-amber-600 text-amber-50",
            "hover:bg-amber-700 active:bg-amber-800",
            "transition-colors duration-150",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
          )}
          aria-label="Retry connection"
        >
          <RefreshCw className="h-3 w-3" aria-hidden="true" />
          Retry
        </button>
      )}
    </div>
  );
}

/**
 * Compact version of the offline indicator.
 * Useful for inline display in specific sections.
 */
export function OfflineIndicator({
  show,
  className,
}: {
  show: boolean;
  className?: string;
}) {
  if (!show) {
    return null;
  }

  return (
    <div
      role="status"
      aria-label="Offline"
      className={cn(
        "inline-flex items-center gap-1.5",
        "rounded-full px-2.5 py-1",
        "bg-amber-100 text-amber-700",
        "text-xs font-medium",
        className
      )}
    >
      <WifiOff className="h-3 w-3" aria-hidden="true" />
      <span>Offline</span>
    </div>
  );
}

export default OfflineBanner;
